"use client";

import { motion } from "framer-motion";
import { Fragment, useMemo } from "react";
import { cardVariants } from "@/lib/animations";

export interface HotelsTableRow {
    id: number;
    /** Aynı otel farklı paketlerde tekrarlanınca benzersiz satır anahtarı */
    rowKey?: string;
    name: string;
    city?: string;
    country?: string;
    nights?: number;
    stars?: number;
    packageName?: string;
    alternativeNames?: string[];
}

interface HotelsTableProps {
    rows: HotelsTableRow[];
    /** Çeviri fonksiyonu (opsiyonel) */
    t?: (key: string, fallback?: string) => string;
}

/** Paket adını gösterim için biçimler: sondaki " Package" kaldırılır, her kelimenin ilk harfi büyük. */
const formatPackageLabel = (packageName?: string): string => {
    const name = (packageName ?? "").trim().replace(/\s+Package\s*$/i, "").trim();
    if (!name) return "";
    return name.replace(/\b\w/g, (c) => c.toUpperCase());
};

const renderStars = (stars?: number): string => {
    const n = Math.max(0, Math.min(5, Math.round(stars ?? 0)));
    return n > 0 ? "★".repeat(n) : "";
};

/** Sıra korunarak anahtar bazlı gruplama */
function groupByOrdered<T>(items: T[], keyOf: (item: T) => string): Array<{ key: string; items: T[] }> {
    const map = new Map<string, T[]>();
    const order: string[] = [];
    items.forEach((item) => {
        const key = keyOf(item);
        if (!map.has(key)) {
            map.set(key, []);
            order.push(key);
        }
        map.get(key)!.push(item);
    });
    return order.map((key) => ({ key, items: map.get(key)! }));
}

/**
 * Tura dahil otelleri Paket → Ülke hiyerarşisiyle semantic tablo olarak gösterir.
 * Excel yapısını (Package · Country · City · Hotel · Nights · Alternative) birebir yansıtır.
 * SEO (gerçek <table>), performans (görsel yok) ve okunabilirlik için optimize.
 */
export default function HotelsTable({ rows, t }: HotelsTableProps) {
    const label = (key: string, fallback: string) => (t ? t(key, fallback) : fallback);

    const packageGroups = useMemo(
        () => groupByOrdered(rows, (r) => (r.packageName ?? "").trim().toUpperCase()),
        [rows],
    );
    const anyAlternative = rows.some((r) => (r.alternativeNames?.length ?? 0) > 0);
    const anyCountry = rows.some((r) => (r.country ?? "").trim() !== "");

    if (rows.length === 0) return null;

    return (
        <div className="space-y-6">
            {packageGroups.map((pkg, pi) => {
                const pkgLabel = formatPackageLabel(pkg.key);
                const countryGroups = groupByOrdered(pkg.items, (r) => (r.country ?? "").trim());
                return (
                    <motion.div
                        key={pkg.key || `pkg-${pi}`}
                        variants={cardVariants}
                        className="w-full overflow-hidden rounded-xl border border-gray-200 shadow-sm"
                    >
                        {pkgLabel ? (
                            <div className="flex items-center gap-2 bg-alsero-dark-gray px-4 py-3 sm:px-6">
                                <span className="text-base font-bold tracking-wide text-white sm:text-lg">{pkgLabel}</span>
                                <span className="text-sm font-semibold text-white/80">{label("reservation.package", "Package")}</span>
                            </div>
                        ) : null}
                        <div className="w-full overflow-x-auto overscroll-x-contain [-webkit-overflow-scrolling:touch]">
                            <table className="w-full min-w-[520px] border-collapse bg-white text-left">
                                <thead>
                                    <tr className="border-b border-gray-200 bg-gray-50">
                                        <th scope="col" className="w-[22%] px-3 py-2.5 text-xs font-semibold tracking-wide text-alsero-light-gray sm:px-4 sm:py-3">
                                            {label("tour_detail.hotel_city", "City")}
                                        </th>
                                        <th scope="col" className="px-3 py-2.5 text-xs font-semibold tracking-wide text-alsero-light-gray sm:px-4 sm:py-3">
                                            {label("tour_detail.hotel", "Hotel")}
                                        </th>
                                        <th scope="col" className="w-[10%] whitespace-nowrap px-3 py-2.5 text-center text-xs font-semibold tracking-wide text-alsero-light-gray sm:px-4 sm:py-3">
                                            {label("tour_detail.nights", "Nights")}
                                        </th>
                                        {anyAlternative ? (
                                            <th scope="col" className="w-[28%] px-3 py-2.5 text-xs font-semibold tracking-wide text-alsero-light-gray sm:px-4 sm:py-3">
                                                {label("tour_detail.alternative_hotel", "Alternative")}
                                            </th>
                                        ) : null}
                                    </tr>
                                </thead>
                                <tbody>
                                    {countryGroups.map((country) => (
                                        <Fragment key={`cg-${pkg.key}-${country.key}`}>
                                            {anyCountry && country.key ? (
                                                <tr className="bg-alsero-first/5">
                                                    <th
                                                        scope="colgroup"
                                                        colSpan={anyAlternative ? 4 : 3}
                                                        className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-alsero-first sm:px-4"
                                                    >
                                                        {country.key}
                                                    </th>
                                                </tr>
                                            ) : null}
                                            {country.items.map((row, ri) => {
                                                const stars = renderStars(row.stars);
                                                return (
                                                    <tr
                                                        key={row.rowKey ?? row.id}
                                                        className={`${ri % 2 === 0 ? "bg-white" : "bg-gray-50/50"} border-b border-gray-100 transition-colors last:border-0 hover:bg-alsero-first/5`}
                                                    >
                                                        <td className="px-3 py-3 align-top text-xs text-alsero-light-gray sm:px-4 sm:text-sm">
                                                            {row.city || "—"}
                                                        </td>
                                                        <td className="px-3 py-3 align-top sm:px-4">
                                                            <span className="text-sm font-semibold text-alsero-dark-gray">{row.name}</span>
                                                            {stars ? (
                                                                <span className="ml-2 whitespace-nowrap text-xs text-yellow-500" aria-label={`${Math.round(row.stars ?? 0)} star`}>
                                                                    {stars}
                                                                </span>
                                                            ) : null}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-3 text-center align-top text-xs text-alsero-dark-gray tabular-nums sm:px-4 sm:text-sm">
                                                            {row.nights != null ? row.nights : "—"}
                                                        </td>
                                                        {anyAlternative ? (
                                                            <td className="px-3 py-3 align-top text-xs text-alsero-dark-gray/80 sm:px-4 sm:text-sm">
                                                                {row.alternativeNames && row.alternativeNames.length > 0
                                                                    ? row.alternativeNames.join(", ")
                                                                    : "—"}
                                                            </td>
                                                        ) : null}
                                                    </tr>
                                                );
                                            })}
                                        </Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </motion.div>
                );
            })}
        </div>
    );
}
